<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  canManage: { type: Boolean, default: false },
  catalog: { type: Object, default: () => ({}) },
  compact: { type: Boolean, default: false },
  topic: { type: Object, required: true },
});

const emit = defineEmits(['delete', 'edit']);
const { t } = useI18n();

const optionLabel = (options, id) =>
  options.find(option => String(option.id) === String(id))?.label;

const topicLabel = computed(
  () =>
    optionLabel(props.catalog.topics || [], props.topic.topic_code) ||
    props.topic.topic_code
);

const summaryLabels = computed(() => {
  const { data = {}, topic_code: topicCode } = props.topic;

  if (topicCode === 'averbacao') {
    return (data.sellers || []).flatMap(seller =>
      (seller.annotation_type_ids || [])
        .map(id => optionLabel(props.catalog.annotation_types || [], id))
        .filter(Boolean)
    );
  }
  if (topicCode === 'entrega_chaves') {
    return [
      optionLabel(props.catalog.payment_methods || [], data.payment_method),
    ].filter(Boolean);
  }
  if (topicCode === 'taxas') {
    return (data.tax_type_ids || [])
      .map(id => optionLabel(props.catalog.tax_types || [], id))
      .filter(Boolean);
  }
  if (topicCode === 'reforco_sinal') {
    return [
      optionLabel(props.catalog.reinforcement_moments || [], data.moment),
    ].filter(Boolean);
  }
  if (topicCode === 'outros') {
    return [
      optionLabel(props.catalog.other_subjects || [], data.subject),
    ].filter(Boolean);
  }

  return [];
});

const firstLabel = computed(() => summaryLabels.value[0]);
const additionalCount = computed(() =>
  Math.max(summaryLabels.value.length - 1, 0)
);
</script>

<template>
  <article
    :class="
      compact
        ? 'group flex min-h-14 items-center border-b border-lais-border-soft bg-white px-3 py-3 sm:h-14 sm:overflow-hidden'
        : 'bg-white px-5 py-4'
    "
  >
    <div
      :class="
        compact
          ? 'flex w-full min-w-0 items-center justify-between gap-3'
          : 'flex flex-wrap items-center justify-between gap-3'
      "
    >
      <div class="flex min-w-0 flex-wrap items-center gap-2">
        <span
          class="i-lucide-circle-check size-5 shrink-0 text-lais-success"
          aria-hidden="true"
        />
        <p
          class="font-semibold text-lais-text"
          :class="compact ? 'truncate' : ''"
        >
          {{ topicLabel }}
        </p>
        <span
          v-if="firstLabel"
          class="rounded-full bg-lais-bg-soft px-2 py-1 text-xs font-semibold text-lais-text-body"
        >
          {{ firstLabel }}
        </span>
        <span
          v-if="additionalCount"
          class="rounded-full bg-lais-bg-soft px-2 py-1 text-xs font-semibold text-lais-text-body"
        >
          {{
            t('POST_SALE.TOPICS.SUMMARY.MORE_ITEMS', {
              count: additionalCount,
            })
          }}
        </span>
      </div>
      <div
        v-if="canManage"
        :class="
          compact
            ? 'flex shrink-0 gap-1 sm:invisible sm:group-hover:visible sm:group-focus-within:visible'
            : 'flex gap-1'
        "
      >
        <LaisPrimaryButton
          :aria-label="t('POST_SALE.TOPICS.ACTIONS.EDIT')"
          icon="i-lucide-pencil"
          size="sm"
          variant="link"
          @click="emit('edit', topic)"
        />
        <LaisPrimaryButton
          :aria-label="t('POST_SALE.TOPICS.ACTIONS.DELETE')"
          icon="i-lucide-x"
          size="sm"
          variant="link"
          @click="emit('delete', topic)"
        />
      </div>
    </div>
  </article>
</template>
